"use client";

import { capitalize } from "lodash";
import { BarChart, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";

import ExchangeImage from "@/components/global/exchange-swticher/exchangeIcon";
import { AssetType } from "@/components/global/seachDialog";
import LoadingSpinner from "@/components/loading-spinner/loading-spinner";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { formatNumber } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import {
  createAlert,
  deleteAlerts,
  getAlerts,
} from "@/services/controllers/settings";
import { useAppSelector } from "@/services/hooks/redux.hooks";
import useScreenSize from "@/services/hooks/screen.hooks";

import { CoinsDropdown } from "./coinsDropdown";
import { AlertTriggers, AlertType } from "./types";

export default function AlertContent({
  defaultCoin,
}: {
  defaultCoin?: {
    name: string;
    price: string | number;
    assetId?: number;
  };
}) {
  const { width } = useScreenSize();
  const { toast } = useToast();
  const { connectedKeys } = useAppSelector((state) => state.exchangeSwitcher);

  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [selectedExchange, setSelectedExchange] = useState<string>("");
  const [selectedCoin, setSelectedCoin] = useState<AssetType>();
  const [selectedTrigger, setSelectedTrigger] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (selectedCoin?.currentPrice) {
      setPrice(selectedCoin.currentPrice.toString());
    }
  }, [selectedCoin]);

  useEffect(() => {
    const controller = new AbortController();
    getAlerts({
      signal: controller?.signal,
      params: {
        asset_id: defaultCoin?.assetId,
      },
    })
      .then((response) => {
        setAlerts(response);
        setIsLoading(false);
        if (defaultCoin) {
          setPrice(defaultCoin.price as string);
          setSelectedCoin({
            name: defaultCoin.name,
            change14D: 0,
            change24H: 0,
            change7D: 0,
            currentPrice: defaultCoin.price as number,
            exposureName: defaultCoin.name,
            iconUrl: "",
            id: defaultCoin.assetId as number,
            isFavorite: false,
            mrkCap: 0,
            pnl: 0,
            price: defaultCoin.price as number,
            rank: 0,
            symbol: "",
            volume: 0,
          });
        }
      })
      .catch((error) => {
        if (error.code !== "ERR_CANCELED" && error.code != null) {
          setIsLoading(false);
        }
      });
    return () => {
      controller.abort();
    };
  }, [defaultCoin]);

  const onSave = useCallback(() => {
    const exchange = connectedKeys.find(
      (e) => e.exchange_key_id === parseFloat(selectedExchange),
    );
    if (!exchange?.provider_id) {
      toast({
        title: m.warning(),
        description: m.late_jolly_bullock_jump(),
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (!selectedCoin?.id) {
      toast({
        title: m.warning(),
        description: m.zany_bland_ox_grasp(),
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (selectedTrigger.trim() === "" || selectedTrigger == null) {
      toast({
        title: m.warning(),
        description: m.happy_patient_gorilla_thrive(),
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (price.trim() === "" || price == null) {
      toast({
        title: m.warning(),
        description: m.dull_pretty_gull_tap(),
        variant: "destructive",
        duration: 2000,
      });
      return;
    }

    setIsAdding(true);
    createAlert({
      data: {
        symbol: selectedCoin.symbol.toUpperCase() + "-USDT",
        alert_condition: selectedTrigger,
        price: price,
        notes: note,
        asset_id: selectedCoin.id,
      },
      params: {
        exchange_key_id: parseFloat(selectedExchange),
      },
    })
      .then((response) => {
        setAlerts((st) => [...st, response]);
        setSelectedExchange("");
        setSelectedTrigger("");
        setPrice("");
        setNote("");
        toast({
          title: m.shy_safe_albatross_adore(),
          description: m.wacky_still_meerkat_cry(),
          variant: "success",
        });
      })
      .finally(() => {
        setIsAdding(false);
      });
  }, [
    connectedKeys,
    price,
    selectedCoin?.id,
    selectedCoin?.symbol,
    selectedExchange,
    selectedTrigger,
    toast,
    note,
  ]);

  const onDelete = useCallback(
    (id: number) => {
      deleteAlerts(id).then(() => {
        setAlerts((st) => st.filter((e) => e.id !== id));
        toast({
          title: m.shy_safe_albatross_adore(),
          description: m.spry_actual_panther_prosper(),
          variant: "success",
          duration: 2000,
        });
      });
    },
    [toast],
  );

  return (
    <>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="w-full space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="exchange">{m.exchange()}</Label>
              <Select
                onValueChange={setSelectedExchange}
                value={selectedExchange}
              >
                <SelectTrigger id="exchange" aria-label="Exchange">
                  <SelectValue placeholder={m.broad_giant_dog_bless()} />
                </SelectTrigger>
                <SelectContent>
                  {connectedKeys
                    .filter((e) => e.provider_id != null)
                    .map((key) => {
                      return (
                        <SelectItem
                          value={key.exchange_key_id?.toString() as string}
                          key={key.exchange_key_id}
                        >
                          <div className="flex items-center gap-2">
                            {key?.account_type === "paper" ? (
                              <Image
                                key={key.name}
                                src="/paper.svg"
                                alt="Paper Account Icon"
                                width={17}
                                height={17}
                              />
                            ) : (
                              <ExchangeImage
                                providerId={key?.provider_id}
                                width={20}
                              />
                            )}
                            <p>{key.name}</p>
                          </div>
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="coin">{m.coin()}</Label>
              <CoinsDropdown
                onSelect={setSelectedCoin}
                defaultCoin={selectedCoin}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="condition">{m.condition()}</Label>
              <Select
                value={selectedTrigger}
                onValueChange={setSelectedTrigger}
              >
                <SelectTrigger id="condition" aria-label="Condition">
                  <SelectValue placeholder={m.only_topical_hyena_transform()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LESS_THAN">
                    {m.teary_awake_moose_yell()}
                  </SelectItem>
                  <SelectItem value="MORE_THAN">
                    {m.great_sharp_dove_charm()}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="price">{m.price()}</Label>
              <div className="flex border rounded-sm shadow-sm">
                <Input
                  id="price"
                  placeholder="0..."
                  className="flex-1 border-0 shadow-none"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
                <span className="flex items-center px-2 border-l rounded-r-md text-muted-foreground text-xs">
                  USDT
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">{m.notes()}</Label>
            <Textarea
              id="notes"
              placeholder={m.upper_nimble_lizard_boost()}
              className="min-h-[100px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <Button
            variant="default"
            className="w-fit min-w-28 mx-auto"
            disabled={isAdding}
            onClick={onSave}
          >
            {isAdding ? <LoadingSpinner /> : m.ornate_caring_barbel_value()}
          </Button>
        </div>

        <Card className="w-full max-w-4xl mx-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{m.coin()}</TableHead>
                <TableHead className="md:table-cell hidden">
                  {m.note()}
                </TableHead>
                <TableHead className="md:tabel-cell hidden">
                  {m.condition()}
                </TableHead>
                <TableHead className="md:hidden table-cell">
                  {m.when()}
                </TableHead>
                <TableHead>{m.price()}</TableHead>
                <TableHead className="md:tabel-cell hidden">
                  {m.exchange()}
                </TableHead>
                <TableHead className="md:hidden table-cell"></TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, index) => {
                  return (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell colSpan={6} className="h-[54px] mb-2">
                        <Skeleton className="w-full h-full" />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : !alerts.length ? (
                <TableRow>
                  <TableCell
                    colSpan={width <= 450 ? 5 : 6}
                    className="text-center py-20"
                  >
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <BarChart className="w-12 h-12 text-muted-foreground" />
                      <p className="text-lg font-medium text-muted-foreground">
                        {m.factual_zany_gorilla_pout()}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                alerts.map((alert) => {
                  const exchange = connectedKeys.find(
                    (e) =>
                      e.exchange_key_id ===
                      (alert.exchange_key_id ?? alert.exchangeKeyId),
                  );

                  return (
                    <TableRow key={alert.id} className="h-[52px]">
                      <TableCell>{alert.symbol}</TableCell>
                      <TableCell className="md:table-cell hidden">
                        {alert.notes === "" ? (
                          <span>&mdash;</span>
                        ) : (
                          alert.notes
                        )}
                      </TableCell>
                      <TableCell className="md:table-cell hidden">
                        {
                          AlertTriggers[
                            alert.alert_condition as keyof typeof AlertTriggers
                          ]
                        }
                      </TableCell>
                      <TableCell className="md:hidden table-cell">
                        {alert.alert_condition === "LESS_THAN" ? (
                          <TrendingDown />
                        ) : (
                          <TrendingUp />
                        )}
                      </TableCell>
                      <TableCell>
                        {formatNumber(alert.price, true, false)}
                      </TableCell>
                      <TableCell className="flex items-center gap-1 h-[52px]">
                        {exchange?.account_type === "paper" ? (
                          <Image
                            key={exchange.name}
                            src="/paper.svg"
                            alt="Paper Account Icon"
                            width={17}
                            height={17}
                          />
                        ) : (
                          <ExchangeImage
                            providerId={exchange?.provider_id}
                            width={16}
                          />
                        )}
                        <span className="md:block hidden max-w-[10ch] truncate">
                          {capitalize(exchange?.name)}
                        </span>
                      </TableCell>
                      <TableCell className="cursor-pointer">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Trash2 className="w-4 h-4" />
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {m.ok_alive_cowfish_fry()}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {m.busy_livid_grebe_hunt()}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {m.cancel()}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(alert.id)}
                              >
                                {m.confirm()}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </>
  );
}
